import { createPersistedStore, type PersistedStoreHook } from '@tasama/utils';
import type { BaseStoreState } from '@tasama/utils';
import {
  yourDetailsDefaultValues,
  type YourDetailsFormValues,
} from '../schemas/your-details.schema';

/**
 * Thin coordinator store for YourDetails.
 *
 * The full validation logic has moved to react-hook-form inside YourDetailsPage.tsx.
 * This store exists only to:
 *  - Expose isValid to use-step-validation.hook.ts (stepper gating)
 *  - Expose formData to use-onboarding-payload.hook.ts (API payload building)
 *  - Expose setFieldError / markFieldAsInteracted to use-onboarding-api.hook.ts (API error injection)
 *  - Expose resetForm() to store-manager.ts (clearing stores on company type change)
 */

interface YourDetailsState extends BaseStoreState {
  formData: Partial<YourDetailsFormValues>;
  isValid: boolean;
  pendingApiError: { field: string; message: string } | null;
}

export interface YourDetailsStore extends YourDetailsState {
  setValidity: (_isValid: boolean) => void;
  setFormData: (_data: Partial<YourDetailsFormValues>) => void;
  setFieldError: (_field: string, _message: string | null) => void;
  clearApiError: () => void;
  // No-op kept for use-onboarding-api.hook.ts compatibility
  markFieldAsInteracted: (_field: string) => void;
  resetForm: () => void;
}

const initialFormData = yourDetailsDefaultValues;

export const useYourDetailsStore = createPersistedStore<YourDetailsState>(
  'your-details',
  {
    formData: initialFormData,
    isValid: false,
    pendingApiError: null,
  },
  (set) => ({
    setValidity: (isValid: boolean) => set({ isValid }),

    setFormData: (data: Partial<YourDetailsFormValues>) =>
      set((state: YourDetailsState) => ({
        formData: { ...state.formData, ...data },
      })),

    setFieldError: (field: string, message: string | null) =>
      set({ pendingApiError: message ? { field, message } : null }),

    clearApiError: () => set({ pendingApiError: null }),

    // No-op: interacted state is owned by react-hook-form; kept for use-onboarding-api compatibility
    markFieldAsInteracted: (_field: string) => {},

    resetForm: () =>
      set({ formData: initialFormData, isValid: false, pendingApiError: null }),
  }),
  {
    partialize: (state: YourDetailsState) => ({
      formData: state.formData,
      isValid: state.isValid,
      // pendingApiError intentionally excluded — transient, not persisted
    }),
  }
) as unknown as PersistedStoreHook<YourDetailsStore>;

// Selectors — interfaces with use-step-validation and use-onboarding-payload unchanged
export const useYourDetailsValues = () =>
  useYourDetailsStore((state: YourDetailsState) => state.formData);
export const useYourDetailsErrors = () =>
  ({}) as Partial<Record<keyof YourDetailsFormValues, string>>;
export const useYourDetailsIsValid = () =>
  useYourDetailsStore((state: YourDetailsState) => state.isValid);
export const useYourDetailsIsDirty = () => false;
export const useYourDetailsLoading = () => false;
